import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useLocalizeMessage, Badge, Link } from 'libs.nucleus';
import { isUnmodifiedPrimaryClick, shouldInterceptDocumentOpen } from '../../utils/signed_document_link.helpers';
import { statusLabelMap, statusMap } from '../../utils/signed_document_status.helpers';
const hasUploadedFinalDocument = (document) => Boolean(document.ec__final_document?.url);
/** First five classic list columns — keep sticky while scrolling the remaining fields. */
export const SITE_DOCUMENTS_PINNED_COLUMN_IDS = [
// TODO: Reinstate this when https://jira.devops.medable.com/browse/DLS-267 is fixed.
// 'identifier',
// 'participantNameOrEmail',
// 'participantId',
// 'title',
// 'status',
];
export const useSiteDocumentsTableColumns = ({ onOpenDocument, onOpenPtsDocument, canOpenDocument = true, requiresIcfAccessWarning = false, onOpenDocumentDetails, getDocumentDetailsHref, }) => {
    const translate = useLocalizeMessage();
    const siteDocumentsColumns = useMemo(() => {
        const renderDetailsLink = (document, openDetails, label, dataTestId) => (_jsx(Link
        // A real href so ⌘/ctrl-click, middle-click and Open in new tab work; the
        // handler below still routes an ordinary click through the SPA.
        , { 
            // A real href so ⌘/ctrl-click, middle-click and Open in new tab work; the
            // handler below still routes an ordinary click through the SPA.
            href: getDocumentDetailsHref?.(document) ?? '#', "data-testid": dataTestId, onClick: (event) => {
                if (!isUnmodifiedPrimaryClick(event)) {
                    return;
                }
                event.preventDefault();
                openDetails(document);
            }, children: label }));
        return [
            {
                header: translate('label.site_documents.identifier'),
                accessorKey: 'identifier',
                cell: (context) => {
                    const document = context.row.original;
                    const identifier = document.identifier ?? document.ec__identifier ?? '';
                    if (!canOpenDocument) {
                        return _jsx("span", { children: identifier });
                    }
                    // The details page is the classic destination for the identifier; opening a
                    // document straight into a modal stays the fallback for callers without a route.
                    if (onOpenDocumentDetails) {
                        return renderDetailsLink(document, onOpenDocumentDetails, identifier, 'site-document-details-link');
                    }
                    if (document.ec__pts) {
                        return (_jsx(Link, { href: '#', "data-testid": 'site-pts-document-link', onClick: (event) => {
                                event.preventDefault();
                                onOpenPtsDocument(document);
                            }, children: identifier }));
                    }
                    return (_jsx(Link, { href: '#', "data-testid": 'site-document-link', onClick: (event) => {
                            if (!shouldInterceptDocumentOpen(event, requiresIcfAccessWarning)) {
                                return;
                            }
                            event.preventDefault();
                            onOpenDocument(document);
                        }, children: identifier }));
                },
            },
            // Sorted on indexed copies stored on the signed document. Source version and source
            // label stay unsorted: there is no index slot left for a copy of those values.
            {
                header: translate('label.site_documents.participant_name_or_email'),
                accessorKey: 'participantNameOrEmail',
                cell: (context) => context.row.original.participantNameOrEmail ?? '',
            },
            {
                header: translate('label.site_documents.participant_id'),
                accessorKey: 'participantId',
                cell: (context) => context.row.original.participantId ?? '',
            },
            {
                header: translate('generic.title'),
                accessorKey: 'title',
                cell: (context) => {
                    const document = context.row.original;
                    const title = document.title ?? '';
                    return (_jsx("span", { className: 'whitespace-nowrap', children: canOpenDocument && onOpenDocumentDetails && title
                            ? renderDetailsLink(document, onOpenDocumentDetails, title, 'site-document-title-details-link')
                            : title }));
                },
            },
            {
                header: translate('generic.status'),
                accessorKey: 'status',
                cell: (context) => {
                    const status = context.row.original.status ?? context.row.original.ec__status ?? '';
                    const knownStatus = status;
                    const statusLabelKey = statusLabelMap[knownStatus];
                    return (_jsx(Badge, { variant: statusMap[knownStatus] ?? 'common', label: statusLabelKey ? translate(statusLabelKey) : status }));
                },
            },
            {
                header: translate('label.site_documents.printed_document'),
                accessorKey: 'ec__pts',
                cell: (context) => (context.row.original.ec__pts ? translate('generic.yes') : translate('generic.no')),
            },
            {
                header: translate('label.site_documents.required_signers'),
                accessorKey: 'ec__required_signers',
                cell: (context) => context.row.original.ec__required_signers ?? 0,
            },
            {
                header: translate('label.site_documents.applied_signers'),
                accessorKey: 'appliedSigners',
                cell: (context) => context.row.original.appliedSigners ?? 0,
            },
            {
                header: translate('label.site_documents.source_version'),
                accessorKey: 'sourceVersion',
                enableSorting: false,
                cell: (context) => context.row.original.sourceVersion ?? '',
            },
            {
                header: translate('label.site_documents.source_label'),
                accessorKey: 'sourceLabel',
                enableSorting: false,
                cell: (context) => context.row.original.sourceLabel ?? '',
            },
            {
                header: translate('label.site_documents.printed_document_uploaded'),
                accessorKey: 'ec__final_document',
                cell: (context) => {
                    const document = context.row.original;
                    if (!document.ec__pts) {
                        return translate('label.site_documents.digital_document');
                    }
                    return hasUploadedFinalDocument(document) ? translate('generic.yes') : translate('generic.no');
                },
            },
        ];
    }, [
        canOpenDocument,
        getDocumentDetailsHref,
        onOpenDocument,
        onOpenDocumentDetails,
        onOpenPtsDocument,
        requiresIcfAccessWarning,
        translate,
    ]);
    return { siteDocumentsColumns };
};
